import React, { useState, useRef, useEffect } from 'react';
import { Avatar, Box, TextField, Typography, makeStyles } from '@material-ui/core';
import IconButton from '../../components/IconButton';
import { ICON_BUTTON_TYPE } from '../../constants';
import useStore from '../../Store/Store';
import CompanionPic from '../../images/companion_icon.svg';
import BoyPic from '../../images/boy.png';
import { chatWithCompanion } from '../../utils/api';
import { chatDateFormat } from '../../utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useMutation } from 'react-query';
import { sendCompanionMessage, subscribeToCompanionMessage } from '../../utils/firestoreMessageConfigs';

const useStyles = makeStyles((theme) => ({
  '@keyframes typingDots': {
    '0%': { opacity: 0.3 },
    '50%': { opacity: 1 },
    '100%': { opacity: 0.3 },
  },
  typingDots: {
    '& .typing-dots': {
      animation: '$typingDots 1.4s infinite',
      letterSpacing: '2px',
      fontSize: '20px',
      lineHeight: 1,
    },
  },
  chatbox: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      bottom: 0,
      zIndex: 1300,
      borderRadius: 16,
      boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
      background: '#fff',
    },
  },
  chatboxInner: {
    flex: 1,
    overflowY: 'auto',
  },
  messageBodySender: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: theme.spacing(2),
  },
  messageBodyCompanion: {
    display: 'flex',
    margin: theme.spacing(2),
  },
  senderMessage: {
    maxWidth: '70%',
    marginRight: theme.spacing(1),
    backgroundColor: theme.palette.primary.light,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1),
  },
  companionMessage: {
    maxWidth: '70%',
    marginLeft: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1),
  },
  username: {
    display: 'block',
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5),
  },
  messageTime: {
    marginLeft: theme.spacing(1),
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  inputDiv: {
    display: 'flex',
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    alignItems: 'flex-end',
    gap: theme.spacing(1),
  },
  sendButton: {
    alignSelf: 'flex-end',
    minWidth: '40px',
    height: '40px',
    flexShrink: 0,
  },
  textFieldWrapper: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-end',
  },
}));

const CompanionChatBox = ({ handleClick }) => {
  const classes = useStyles();
  const user = useStore((state) => state.user);
  const scrollRef = useRef();
  const [message, setMessage] = useState('');
  const [chatLoader, setChatLoader] = useState(false);
  const [messages, setMessages] = useState([]);

  React.useEffect(() => {
    const unsubscribe = subscribeToCompanionMessage(user?._id, (snapshot) => {
      const newMessages = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Only update if we have new messages
      if (newMessages.length !== messages.length) {
        const lastMessage = newMessages[newMessages.length - 1];

        // If it's a bot message, wait a tiny bit before removing loader
        // This ensures the message is rendered before loader disappears
        if (lastMessage?.senderId === 'bot') {
          setMessages(newMessages);
          scrollToBottom();
          setTimeout(() => {
            setChatLoader(false);
          }, 300); // Small delay to ensure smooth transition
        } else {
          setMessages(newMessages);
          scrollToBottom();
        }
      }
    });

    return () => (unsubscribe ? unsubscribe() : null);
  }, [user, messages.length]);

  const scrollToBottom = () => {
    setTimeout(() => {
      scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const chatWithCompanionMutation = useMutation(chatWithCompanion, {
    onSuccess: async (data) => {
      try {
        await sendCompanionMessage(user?._id, 'bot', data?.data);
        setMessage('');
        scrollToBottom();
      } catch (error) {
        console.log('error sending companion message:', error);
        setChatLoader(false);
      }
    },
    onError: (error) => {
      console.log('error =====> ', error);
      setChatLoader(false);
      setMessage('');
    },
  });

  const handlePressEnter = async (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      setMessage('');
      getNewMessage();
    }
  };

  const getNewMessage = async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) return;

    try {
      setChatLoader(true);
      await sendCompanionMessage(user?._id, user?._id, trimmedMessage);
      chatWithCompanionMutation.mutate({ userId: user?._id, message: trimmedMessage });
      setMessage('');
      scrollToBottom();
    } catch (error) {
      console.log('error=====>', error);
      setChatLoader(false);
      setMessage('');
    }
  };

  return (
    <Box className={classes.chatbox}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 16px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          <Avatar src={CompanionPic} alt="CAN Companion" sx={{ marginRight: '10px' }} />
          <Typography variant="h6" style={{ marginLeft: '10px' }}>
            CAN Companion
          </Typography>
        </Box>
        <IconButton type={ICON_BUTTON_TYPE.close} onClick={handleClick} sx={{ padding: '4px' }} />
      </Box>
      <Box className={classes.chatboxInner}>
        {messages.map(({ text, senderId, createdAt }, idx) => (
          <Box key={idx}>
            {senderId === user?._id ? (
              <Box className={classes.messageBodySender}>
                <Box className={classes.senderMessage}>
                  <Typography className={classes.username}>
                    {user?.userInfo?.firstName || ''} {'(you)'}
                    <span className={classes.messageTime}>{chatDateFormat(createdAt)}</span>
                  </Typography>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
                </Box>
                <Avatar src={user?.userInfo?.imageUrl || BoyPic} alt="dp" />
              </Box>
            ) : (
              <Box className={classes.messageBodyCompanion}>
                <Avatar src={CompanionPic} alt="dp" />
                <Box className={classes.companionMessage}>
                  <Typography className={classes.username}>
                    CAN Companion
                    <span className={classes.messageTime}>{chatDateFormat(createdAt)}</span>
                  </Typography>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
                </Box>
              </Box>
            )}
          </Box>
        ))}
        {chatLoader && (
          <Box className={classes.messageBodyCompanion}>
            <Avatar src={CompanionPic} alt="dp" />
            <Box className={`${classes.companionMessage} ${classes.typingDots}`}>
              <Typography className={classes.username}>CAN Companion</Typography>
              <Typography
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: 'rgba(0, 0, 0, 0.6)',
                  fontStyle: 'italic',
                }}
              >
                Thinking<span className="typing-dots">...</span>
              </Typography>
            </Box>
          </Box>
        )}
        <div ref={scrollRef} />
      </Box>

      <Box className={classes.inputDiv}>
        <Box className={classes.textFieldWrapper}>
          <TextField
            fullWidth
            size="small"
            variant="standard"
            multiline
            minRows={1}
            maxRows={6}
            placeholder={chatLoader ? 'Generating response...' : 'Type a message to CAN Companion...'}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handlePressEnter}
            value={message}
            InputProps={{
              disableUnderline: true,
              style: {
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
                resize: 'none',
                paddingRight: '8px',
              },
            }}
            inputProps={{
              style: {
                padding: 0,
                lineHeight: 1.5,
                maxHeight: '120px',
                overflowY: 'auto',
              },
            }}
          />
        </Box>
        <IconButton
          className={classes.sendButton}
          type={ICON_BUTTON_TYPE.send}
          onClick={getNewMessage}
          disabled={chatLoader}
        />
      </Box>
    </Box>
  );
};

export default CompanionChatBox;
